/**
 * ChangeIndicator Component
 * Shows a small avatar on nodes that were recently changed by other users
 */

import { useState, useEffect, useMemo } from 'react'
import { Avatar, Tooltip, Fade, Box } from '@mui/material'
import { useReactFlow } from '@xyflow/react'
import type { FlowChange } from '@services/flowHub.service'
import type { WorkflowNode } from '@store/workflowStore'

// Duration to show the indicator (5 seconds)
const INDICATOR_DURATION = 5000

export interface RecentChange {
  id: string
  nodeId?: string
  edgeId?: string
  userId: string
  userName: string
  type: 'add' | 'move' | 'update' | 'delete'
  position?: { x: number; y: number } // For deleted nodes, store last known position
  expiresAt: number
}

/**
 * Get initials from a name
 */
function getInitials(name: string): string {
  const parts = name.split(' ').filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

/**
 * Generate a consistent color based on user ID
 */
function getUserColor(userId: string): string {
  const colors = [
    '#1976d2', // blue
    '#388e3c', // green
    '#f57c00', // orange
    '#7b1fa2', // purple
    '#c2185b', // pink
    '#0097a7', // cyan
    '#5d4037', // brown
    '#455a64', // blue-grey
  ]

  let hash = 0
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash)
  }

  return colors[Math.abs(hash) % colors.length]
}

/**
 * Get action label for tooltip
 */
function getActionLabel(type: RecentChange['type']): string {
  switch (type) {
    case 'add':
      return 'added this'
    case 'move':
      return 'moved this'
    case 'update':
      return 'updated this'
    case 'delete':
      return 'deleted this'
  }
}

interface NodeChangeIndicatorProps {
  change: RecentChange
}

/**
 * Small avatar indicator shown on a node
 */
export function NodeChangeIndicator({ change }: NodeChangeIndicatorProps) {
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    const remaining = change.expiresAt - Date.now()
    if (remaining <= 0) {
      setVisible(false)
      return
    }

    const timer = setTimeout(() => {
      setVisible(false)
    }, remaining)

    return () => clearTimeout(timer)
  }, [change.expiresAt])

  if (!visible) return null

  return (
    <Fade in={visible} timeout={300}>
      <Tooltip title={`${change.userName} ${getActionLabel(change.type)}`} placement="top">
        <Avatar
          sx={{
            position: 'absolute',
            top: -8,
            right: -8,
            width: 24,
            height: 24,
            fontSize: 10,
            bgcolor: getUserColor(change.userId),
            border: '2px solid white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            zIndex: 10,
            cursor: 'default',
            animation: 'pulse 2s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': {
                transform: 'scale(1)',
              },
              '50%': {
                transform: 'scale(1.1)',
              },
            },
          }}
        >
          {getInitials(change.userName)}
        </Avatar>
      </Tooltip>
    </Fade>
  )
}

interface GhostDeleteIndicatorProps {
  change: RecentChange
}

/**
 * Ghost avatar shown where a deleted node used to be
 */
export function GhostDeleteIndicator({ change }: GhostDeleteIndicatorProps) {
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    const remaining = change.expiresAt - Date.now()
    if (remaining <= 0) {
      setVisible(false)
      return
    }

    const timer = setTimeout(() => {
      setVisible(false)
    }, remaining)

    return () => clearTimeout(timer)
  }, [change.expiresAt])

  if (!visible || !change.position) return null

  return (
    <Fade in={visible} timeout={300}>
      <Box
        sx={{
          position: 'absolute',
          left: change.position.x,
          top: change.position.y,
          transform: 'translate(-50%, -50%)',
          zIndex: 5,
          pointerEvents: 'none',
        }}
      >
        <Tooltip title={`${change.userName} deleted a node here`} placement="top" open>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              opacity: 0.7,
              animation: 'fadeOut 5s ease-out forwards',
              '@keyframes fadeOut': {
                '0%': { opacity: 0.8 },
                '70%': { opacity: 0.6 },
                '100%': { opacity: 0 },
              },
            }}
          >
            <Avatar
              sx={{
                width: 40,
                height: 40,
                fontSize: 14,
                bgcolor: getUserColor(change.userId),
                border: '2px dashed rgba(255,255,255,0.8)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              }}
            >
              {getInitials(change.userName)}
            </Avatar>
            <Box
              sx={{
                bgcolor: 'error.main',
                color: 'white',
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontSize: 10,
                fontWeight: 'bold',
                textTransform: 'uppercase',
              }}
            >
              Deleted
            </Box>
          </Box>
        </Tooltip>
      </Box>
    </Fade>
  )
}

/**
 * Hook to manage recent changes with auto-expiry
 */
export function useRecentChanges() {
  const [recentChanges, setRecentChanges] = useState<RecentChange[]>([])

  // Clean up expired changes periodically
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now()
      setRecentChanges(prev => prev.filter(c => c.expiresAt > now))
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  const addChange = (change: FlowChange, nodePosition?: { x: number; y: number }) => {
    const { changeData, userId, userName } = change

    let recentChange: RecentChange | null = null

    switch (changeData.type) {
      case 'node_add':
        recentChange = {
          id: change.id,
          nodeId: changeData.node.id,
          userId,
          userName,
          type: 'add',
          expiresAt: Date.now() + INDICATOR_DURATION,
        }
        break

      case 'node_move':
        recentChange = {
          id: change.id,
          nodeId: changeData.nodeId,
          userId,
          userName,
          type: 'move',
          expiresAt: Date.now() + INDICATOR_DURATION,
        }
        break

      case 'node_update':
        recentChange = {
          id: change.id,
          nodeId: changeData.nodeId,
          userId,
          userName,
          type: 'update',
          expiresAt: Date.now() + INDICATOR_DURATION,
        }
        break

      case 'node_delete':
        recentChange = {
          id: change.id,
          nodeId: changeData.nodeId,
          userId,
          userName,
          type: 'delete',
          position: nodePosition,
          expiresAt: Date.now() + INDICATOR_DURATION,
        }
        break

      case 'edge_add':
      case 'edge_delete':
      case 'edge_update':
        // Could add edge indicators later if needed
        break
    }

    if (recentChange) {
      setRecentChanges(prev => {
        // Remove any existing change for the same node to avoid duplicates
        const filtered = prev.filter(c => c.nodeId !== recentChange!.nodeId)
        return [...filtered, recentChange!]
      })
    }
  }

  const getChangeForNode = (nodeId: string): RecentChange | undefined => {
    return recentChanges.find(c => c.nodeId === nodeId && c.type !== 'delete')
  }

  const getDeleteChanges = (): RecentChange[] => {
    return recentChanges.filter(c => c.type === 'delete')
  }

  return {
    recentChanges,
    addChange,
    getChangeForNode,
    getDeleteChanges,
  }
}

interface ChangeIndicatorOverlayProps {
  getChangeForNode: (nodeId: string) => RecentChange | undefined
  getDeleteChanges: () => RecentChange[]
}

/**
 * Overlay component that renders change indicators on top of nodes
 * Must be rendered inside ReactFlow component
 */
export function ChangeIndicatorOverlay({
  getChangeForNode,
  getDeleteChanges,
}: ChangeIndicatorOverlayProps) {
  const { getNodes, getViewport } = useReactFlow<WorkflowNode>()
  const [renderKey, setRenderKey] = useState(0)

  // Force re-render periodically to update indicator visibility and node positions
  // Use a longer interval to reduce re-renders
  useEffect(() => {
    const interval = setInterval(() => {
      setRenderKey(n => n + 1)
    }, 500) // Less frequent updates - indicators don't need real-time tracking
    return () => clearInterval(interval)
  }, [])

  const nodes = getNodes()
  const deleteChanges = getDeleteChanges()
  const viewport = getViewport() // Get viewport on-demand instead of subscribing

  // Convert flow coordinates to screen coordinates
  const flowToScreen = (x: number, y: number) => ({
    x: x * viewport.zoom + viewport.x,
    y: y * viewport.zoom + viewport.y,
  })

  // Get indicators for existing nodes
  const nodeIndicators = useMemo(() => {
    return nodes
      .map(node => {
        const change = getChangeForNode(node.id)
        if (!change) return null

        // Get node dimensions (approximate - nodes are typically ~200x80)
        const nodeWidth = 200

        // Position at top-right corner of node (in flow coordinates)
        const flowPos = {
          x: node.position.x + nodeWidth - 12,
          y: node.position.y - 12,
        }

        return {
          node,
          change,
          flowPos,
        }
      })
      .filter(Boolean) as Array<{
        node: WorkflowNode
        change: RecentChange
        flowPos: { x: number; y: number }
      }>
  }, [nodes, getChangeForNode])

  // Convert positions to screen coordinates
  const screenIndicators = nodeIndicators.map(({ node, change, flowPos }) => ({
    node,
    change,
    screenPos: flowToScreen(flowPos.x, flowPos.y),
  }))

  const screenDeleteChanges = deleteChanges.map(change => ({
    change,
    screenPos: change.position ? flowToScreen(change.position.x, change.position.y) : null,
  }))

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 5,
        overflow: 'hidden',
      }}
    >
      {/* Node change indicators */}
      {screenIndicators.map(({ node, change, screenPos }) => (
        <Box
          key={`indicator-${node.id}`}
          sx={{
            position: 'absolute',
            left: screenPos.x,
            top: screenPos.y,
            pointerEvents: 'auto',
            transform: `scale(${Math.min(1, viewport.zoom)})`,
            transformOrigin: 'top left',
          }}
        >
          <NodeChangeIndicator change={change} />
        </Box>
      ))}

      {/* Ghost indicators for deleted nodes */}
      {screenDeleteChanges.map(({ change, screenPos }) =>
        screenPos && (
          <Box
            key={`ghost-${change.id}`}
            sx={{
              position: 'absolute',
              left: screenPos.x,
              top: screenPos.y,
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
            }}
          >
            <GhostDeleteIndicatorContent change={change} />
          </Box>
        )
      )}
    </Box>
  )
}

/**
 * Inner content for ghost delete indicator (without positioning)
 */
function GhostDeleteIndicatorContent({ change }: { change: RecentChange }) {
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    const remaining = change.expiresAt - Date.now()
    if (remaining <= 0) {
      setVisible(false)
      return
    }

    const timer = setTimeout(() => {
      setVisible(false)
    }, remaining)

    return () => clearTimeout(timer)
  }, [change.expiresAt])

  if (!visible) return null

  return (
    <Fade in={visible} timeout={300}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          opacity: 0.8,
          animation: 'fadeOutGhost 5s ease-out forwards',
          '@keyframes fadeOutGhost': {
            '0%': { opacity: 0.8 },
            '70%': { opacity: 0.5 },
            '100%': { opacity: 0 },
          },
        }}
      >
        <Avatar
          sx={{
            width: 40,
            height: 40,
            fontSize: 14,
            bgcolor: getUserColor(change.userId),
            border: '2px dashed rgba(255,255,255,0.8)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          }}
        >
          {getInitials(change.userName)}
        </Avatar>
        <Box
          sx={{
            bgcolor: 'error.main',
            color: 'white',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            fontSize: 10,
            fontWeight: 'bold',
            textTransform: 'uppercase',
          }}
        >
          Deleted
        </Box>
      </Box>
    </Fade>
  )
}
